import { useAccountContext } from "./useAccountContext";
import { useState } from "react";
import { useAuthContext } from "./useAuthContext";
import { useSettingsContext } from "./useSettingsContext";
import { generateUniqueID } from "../components/SurveyBuilderComponent/CreateSurvey/CreateSurveyFunctions";
import axios from "axios";

export const useHRAdminEmployees = () => {
  const {
    username,
    user,
    clientApi,
    dispatch: authDispatch,
  } = useAuthContext();
  const { account } = useAccountContext();
  const [isLoading, setIsLoading] = useState(false);
  const { isWinAuth } = useSettingsContext();

  const axiosInstance = axios.create({
    baseURL: `${clientApi}`,
    headers: {
      accepts: "application/json",
      "Content-Type": "application/json",
    },
  });

  axiosInstance.interceptors.request.use(
    (request) => {
      const accessToken = user.token;
      if (accessToken) {
        request.headers["Authorization"] = `Bearer ${accessToken}`;
      }
      return request;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  axiosInstance.interceptors.response.use(
    response => response, // Directly return successful responses.
    async error => {
      const originalRequest = error.config;
      if (error?.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true; // Mark the request as retried to avoid infinite loops.
        try {
          if (!isWinAuth) {
            // Make a request to your auth server to refresh the token.
            const response = await axiosInstance.post(`${clientApi}api/auth/refresh`, user);
            // Store the new access and refresh tokens.
            authDispatch({ type: 'Refresh', payload: response.data, clientApi: clientApi, username: username });

            // Update the authorization header with the new access token.
            axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
          }
          else {
            const newToken = await acquireSilentToken();
            let tmpUser = user;
            tmpUser.token = newToken;
            authDispatch({ type: 'REFRESH', payload: tmpUser, clientApi: clientApi, username: username });
            axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
          }
          return axios(originalRequest); // Retry the original request with the new access token.
        } catch (refreshError) {
          // Handle refresh token errors by clearing stored tokens and redirecting to the login page.
          console.error("Token refresh failed:", refreshError);
          authDispatch({ type: "LOGOUT" });
          return Promise.reject(refreshError);
        }
      }
      return Promise.reject(error); // For all other errors, return the error as is.
    }
  );

  const getBranches = async (selectedCompany) => {
    try {
      const response = await axiosInstance.get(
        `${clientApi}api/Company/Branches`,
        {
          params: { compid: `'${selectedCompany?.code}'` },
        }
      );
      return response.data;
    } catch (error) {
      console.error(error);
      return [];
    }
  };

  const getLocations = async (selectedCompany) => {
    try {
      const response = await axiosInstance.get(
        `${clientApi}api/Company/Locations`,
        {
          params: { company: `'${selectedCompany?.code}'` },
        }
      );
      return response.data;
    } catch (error) {
      console.error(error);
      return [];
    }
  };

  const getDepartments = async (selectedCompany) => {
    try {
      const response = await axiosInstance.get(
        `${clientApi}api/Company/Departments`,
        {
          params: { company: `'${selectedCompany?.code}'` },
        }
      );
      return response.data;
    } catch (error) {
      console.error(error);
      return [];
    }
  };

  const getSeniorityLevels = async (selectedCompany) => {
    try {
      const response = await axiosInstance.get(
        `${clientApi}api/Company/SeniorityLevels`,
        {
          params: { company: `'${selectedCompany?.code}'` },
        }
      );
      return response.data;
    } catch (error) {
      console.error(error);
      return [];
    }
  };

  const getEmploymentTypes = async (selectedCompany) => {
    try {
      const response = await axiosInstance.get(
        `${clientApi}api/Company/EmploymentTypes`,
        {
          params: { company: selectedCompany?.code },
        }
      );
      return response.data;
    } catch (error) {
      console.error(error);
      return [];
    }
  };

  const getJobTitles = async (selectedCompany) => {
    try {
      const response = await axiosInstance.get(
        `${clientApi}api/Company/JobTitles`,
        {
          params: { company: `'${selectedCompany?.code}'` },
        }
      );
      return response.data;
    } catch (error) {
      console.error(error);
      return [];
    }
  };

  const getEmployees = async (
    selectedBranches = [],
    selectedLocations = [],
    selectedDepartments = [],
    selectedSeniorityLevels = [],
    selectedEmploymentTypes = [],
    selectedJobTitles = [],
    searchQuery = "",
    selectedCompany = "",
    withPhoto = true,
    withInactive = true
  ) => {
    try {
      setIsLoading(true);
      const response = await axiosInstance.get(`${clientApi}api/Employee/AuthEmployees`, {
        params: {
          employeeId: account.employeeId,
          //* if companies is left null, fetching takes 1/2 - 1/3
          //* of time taken by including selected company  
          companies: selectedCompany ?? null,
          branches: selectedBranches?.map(item => `'${item}'`).toString(),
          locations: selectedLocations?.map(item => `'${item}'`).toString(),
          depts: selectedDepartments?.map(item => `'${item}'`).toString(),
          seniorityLevels: selectedSeniorityLevels?.map(item => `'${item}'`).toString(),
          employmentTypes: selectedEmploymentTypes?.map(item => `'${item}'`).toString(),
          jobTitles: selectedJobTitles?.map(item => `'${item}'`).toString(),
          employeeIdOrName: searchQuery,
          withPhoto: withPhoto,
          withInactive: withInactive
        }
      });
      setIsLoading(false);
      return response.data;
    } catch (error) {
      console.error(error);
    };
  };

  const getEmployeeIdBasedOnLocation = async (company, location, branch) => {
    try {
      const response = await axiosInstance.get(
        `${clientApi}api/Employee/GetLatestEmployeeIdPascal`,
        {
          params: { username: username, location: location, company: company, branch: branch },
        }
      );
      return response.data;
    } catch (error) {
      console.error(error);
      return [];
    }
  }

  const getEmployeesInCompany = async ({ company, withPhoto = false }) => {
    try {
      const response = await axiosInstance.get(`${clientApi}api/Employee/GetEmployees`, {
        params: {
          employeeId: account.employeeId,
          companies: company,
        }
      });
      return response.data;
    } catch (error) {
      console.error(error);
    };
  };

  const getEmployeeData = async (employeeId) => {
    try {
      const response = await axiosInstance.get(
        `${clientApi}api/Employee/Employee`,
        {
          params: { username: username, employeeId: employeeId },
        }
      );
      return response.data;
    } catch (error) {
      console.error(error);
      return [];
    }
  };

  const getEmployeeAccumulatedBalances = async (employeeId) => {
    try {
      const response = await axiosInstance.get(
        `${clientApi}api/Employee/AccumulatedBalances`,
        {
          params: { username: username, employeeId: employeeId },
        }
      );
      return response.data;
    } catch (error) {
      console.error(error);
      return [];
    }
  };

  const CalculateEmplRevisedAllowance = async (employeeId, dFrom, dTo) => {
    try {
      const response = await axiosInstance.get(
        `${clientApi}api/Employee/CalculateRevisedAllowance`,
        {
          params: {
            username: username,
            employeeId: employeeId,
            dFrom: dFrom,
            dTo: dTo,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error(error);
      return [];
    }
  };

  const reconcileBalances = async (employeeId, difference, dTo) => {
    try {
      const response = await axiosInstance.get(
        `${clientApi}api/Employee/ReconcileBalances`,
        {
          params: {
            username: username,
            employeeId: employeeId,
            difference: difference,
            dTo: dTo,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error(error);
      return [];
    }
  };

  const getEmployeeServiceLine = async (employeeId) => {
    try {
      const response = await axiosInstance.get(
        `${clientApi}api/Employee/LimitedData`,
        {
          params: { username: username, employeeId: employeeId },
        }
      );
      return response.data;
    } catch (error) {
      console.error(error);
      return [];
    }
  };

  const getMandatoryFields = async (fieldForm) => {
    try {
      const response = await axiosInstance.get(
        `${clientApi}api/Employee/MandatoryFields`,
        {
          params: { username: username, fieldForm: fieldForm },
        }
      );
      return response.data;
    } catch (error) {
      console.error(error);
      return [];
    }
  };

  const getDropdownValues = async (sParams) => {
    let ret;
    await axiosInstance
      .post(`${clientApi}api/Employee/DropdownValues`, sParams, {
        params: {
          username: username,
          employeeId: account.employeeId,
        },
      })
      .then((response) => {
        if (response.status === 200) ret = response.data;
        else ret = [];
      })
      .catch((error) => {
        console.log(error);
        ret = [];
      });

    return ret;
  };

  const getFamilyDetails = async (employeeId) => {
    let ret;
    await axiosInstance
      .get(`${clientApi}api/Employee/FamilyDetails`, {
        params: {
          username: username,
          employeeId: employeeId,
        },
      })
      .then((response) => {
        if (response.status === 200) ret = response;
        else ret = [];
      })
      .catch((error) => {
        console.log(error);
        ret = [];
      });

    return ret;
  };

  const getLimitedData = async (employeeId, control) => {
    let ret;
    await axiosInstance
      .get(`${clientApi}api/Employee/LimitedData`, {
        params: {
          username: username,
          employeeId: employeeId,
          control: control,
        },
      })
      .then((response) => {
        if (response.status === 200) ret = response;
        else ret = [];
      })
      .catch((error) => {
        console.log(error);
        ret = [];
      });

    return ret;
  };

  const getTimescheme = async (employeeId) => {
    let ret;
    await axiosInstance
      .get(`${clientApi}api/Employee/Timescheme`, {
        params: {
          username: username,
          employeeId: employeeId,
        },
      })
      .then((response) => {
        if (response.status === 200) ret = response;
        else ret = [];
      })
      .catch((error) => {
        console.log(error);
        ret = [];
      });

    return ret;
  };

  //#region Schedule
  const getSchedule = async (employeeId) => {
    let ret;
    await axiosInstance
      .get(`${clientApi}api/Employee/Schedule`, {
        params: {
          username: username,
          employeeId: employeeId,
        },
      })
      .then((response) => {
        if (response.status === 200) ret = response;
        else ret = [];
      })
      .catch((error) => {
        console.log(error);
        ret = [];
      });

    return ret;
  };

  const insertSchedule = async (schedule) => {
    const formData = new FormData();
    formData.append("schedule", JSON.stringify({ schedule }));
    try {
      const response = await axiosInstance.post(
        `${clientApi}api/Employee/InsertSchedule`,
        formData,
        {
          params: { username: username, userid: account.employeeId },
          headers: { "Content-Type": "multipart/form-data" },
        }
      );
      return response.data;
    } catch (error) {
      console.error(error);
      return [];
    }
  };

  const deleteSchedule = async (schedule) => {
    const formData = new FormData();

    formData.append("schedule", JSON.stringify({ schedule }));
    try {
      const response = await axiosInstance.post(
        `${clientApi}api/Employee/DeleteSchedule`,
        formData,
        {
          params: { username: username, userid: account.employeeId },
          headers: { "Content-Type": "multipart/form-data" },
        }
      );
      return response.data;
    } catch (error) {
      console.error(error);
      return [];
    }
  };

  const updateSchedule = async (schedule) => {
    const formData = new FormData();
    formData.append("schedule", JSON.stringify({ schedule }));
    try {
      const response = await axiosInstance.post(
        `${clientApi}api/Employee/UpdateSchedule`,
        formData,
        {
          params: { username: username, userid: account.employeeId },
          headers: { "Content-Type": "multipart/form-data" },
        }
      );
      return response.data;
    } catch (error) {
      console.error(error);
      return [];
    }
  };

  //#endregion

  //#region Medical Scheme
  const getMedicalScheme = async (employeeId) => {
    let ret;
    await axiosInstance
      .get(`${clientApi}api/Employee/MedicalScheme`, {
        params: {
          username: username,
          employeeId: employeeId,
        },
      })
      .then((response) => {
        if (response.status === 200) ret = response;
        else ret = [];
      })
      .catch((error) => {
        console.log(error);
        ret = [];
      });

    return ret;
  };

  const insertMedicalScheme = async (medicalScheme) => {
    const formData = new FormData();
    formData.append("medicalScheme", JSON.stringify({ medicalScheme }));
    try {
      const response = await axiosInstance.post(
        `${clientApi}api/Employee/InsertMedicalScheme`,
        formData,
        {
          params: { username: username, userid: account.employeeId },
          headers: { "Content-Type": "multipart/form-data" },
        }
      );
      return response.data;
    } catch (error) {
      console.error(error);
      return [];
    }
  };

  const deleteMedicalScheme = async (medicalScheme) => {
    const formData = new FormData();

    formData.append("medicalScheme", JSON.stringify({ medicalScheme }));
    try {
      const response = await axiosInstance.post(
        `${clientApi}api/Employee/DeleteMedicalScheme`,
        formData,
        {
          params: { username: username, userid: account.employeeId },
          headers: { "Content-Type": "multipart/form-data" },
        }
      );
      return response.data;
    } catch (error) {
      console.error(error);
      return [];
    }
  };

  const updateMedicalScheme = async (medicalScheme) => {
    const formData = new FormData();
    formData.append("medicalScheme", JSON.stringify({ medicalScheme }));
    try {
      const response = await axiosInstance.post(
        `${clientApi}api/Employee/UpdateMedicalScheme`,
        formData,
        {
          params: { username: username, userid: account.employeeId },
          headers: { "Content-Type": "multipart/form-data" },
        }
      );
      return response.data;
    } catch (error) {
      console.error(error);
      return [];
    }
  };

  //#endregion

  //#region Provident Fund
  const getProvidentFund = async (employeeId) => {
    let ret;
    await axiosInstance
      .get(`${clientApi}api/Employee/ProvidentFund`, {
        params: {
          username: username,
          employeeId: employeeId,
        },
      })
      .then((response) => {
        if (response.status === 200) ret = response;
        else ret = [];
      })
      .catch((error) => {
        console.log(error);
        ret = [];
      });

    return ret;
  };

  const insertProvidentFund = async (providentFund) => {
    const formData = new FormData();
    formData.append("providentFund", JSON.stringify({ providentFund }));
    try {
      const response = await axiosInstance.post(
        `${clientApi}api/Employee/InsertProvidentFund`,
        formData,
        {
          params: { username: username, userid: account.employeeId },
          headers: { "Content-Type": "multipart/form-data" },
        }
      );
      return response.data;
    } catch (error) {
      console.error(error);
      return [];
    }
  };

  const deleteProvidentFund = async (providentFund) => {
    const formData = new FormData();

    formData.append("providentFund", JSON.stringify({ providentFund }));
    try {
      const response = await axiosInstance.post(
        `${clientApi}api/Employee/DeleteProvidentFund`,
        formData,
        {
          params: { username: username, userid: account.employeeId },
          headers: { "Content-Type": "multipart/form-data" },
        }
      );
      return response.data;
    } catch (error) {
      console.error(error);
      return [];
    }
  };

  const updateProvidentFund = async (providentFund) => {
    const formData = new FormData();
    formData.append("providentFund", JSON.stringify({ providentFund }));
    try {
      const response = await axiosInstance.post(
        `${clientApi}api/Employee/UpdateProvidentFund`,
        formData,
        {
          params: { username: username, userid: account.employeeId },
          headers: { "Content-Type": "multipart/form-data" },
        }
      );
      return response.data;
    } catch (error) {
      console.error(error);
      return [];
    }
  };

  //#endregion

  //#region EmplDepts

  const insertEmplDepts = async (emplDepts) => {
    const formData = new FormData();

    formData.append("emplDepts", JSON.stringify({ emplDepts }));
    try {
      const response = await axiosInstance.post(
        `${clientApi}api/Employee/InsertEmplDepts`,
        formData,
        {
          params: { username: username, userid: account.employeeId },
          headers: { "Content-Type": "multipart/form-data" },
        }
      );
      return response;
    } catch (error) {
      console.error(error);
      return [];
    }
  };

  const deleteEmplDepts = async (emplDepts) => {
    const formData = new FormData();

    formData.append("emplDepts", JSON.stringify({ emplDepts }));
    try {
      const response = await axiosInstance.post(
        `${clientApi}api/Employee/DeleteEmplDepts`,
        formData,
        {
          params: { username: username, userid: account.employeeId },
          headers: { "Content-Type": "multipart/form-data" },
        }
      );
      return response;
    } catch (error) {
      console.error(error);
      return [];
    }
  };

  const updateEmplDepts = async (emplDepts) => {
    const formData = new FormData();

    formData.append("emplDepts", JSON.stringify({ emplDepts }));
    try {
      const response = await axiosInstance.post(
        `${clientApi}api/Employee/UpdateEmplDepts`,
        formData,
        {
          params: { username: username, userid: account.employeeId },
          headers: { "Content-Type": "multipart/form-data" },
        }
      );
      return response;
    } catch (error) {
      console.error(error);
      return [];
    }
  };

  const getEmplDepts = async (employeeId) => {
    let ret;
    await axiosInstance
      .get(`${clientApi}api/Employee/EmplDepts`, {
        params: {
          username: username,
          employeeId: employeeId,
        },
      })
      .then((response) => {
        if (response.status === 200) ret = response;
        else ret = [];
      })
      .catch((error) => {
        console.log(error);
        ret = [];
      });

    return ret;
  };

  //#endregion

  //#region Family Details

  const insertFamilyDetails = async (familyDetails) => {
    const formData = new FormData();

    formData.append("familyDetails", JSON.stringify({ familyDetails }));
    try {
      const response = await axiosInstance.post(
        `${clientApi}api/Employee/InsertFamily`,
        formData,
        {
          params: { username: username, userid: account.employeeId },
          headers: { "Content-Type": "multipart/form-data" },
        }
      );
      return response.data;
    } catch (error) {
      console.error(error);
      return [];
    }
  };

  const deleteFamilyDetails = async (familyDetails) => {
    const formData = new FormData();

    formData.append("familyDetails", JSON.stringify({ familyDetails }));
    try {
      const response = await axiosInstance.post(
        `${clientApi}api/Employee/DeleteFamily`,
        formData,
        {
          params: { username: username, userid: account.employeeId },
          headers: { "Content-Type": "multipart/form-data" },
        }
      );
      return response.data;
    } catch (error) {
      console.error(error);
      return [];
    }
  };

  const updateFamilyDetails = async (familyDetails) => {
    const formData = new FormData();

    formData.append("familyDetails", JSON.stringify({ familyDetails }));
    try {
      const response = await axiosInstance.post(
        `${clientApi}api/Employee/UpdateFamily`,
        formData,
        {
          params: { username: username, userid: account.employeeId },
          headers: { "Content-Type": "multipart/form-data" },
        }
      );
      return response.data;
    } catch (error) {
      console.error(error);
      return [];
    }
  };

  //#endregion

  //#region 13th Salary Eligibility
  const updateSalaryEligibility = async (salaryEligibility, employeeid) => {
    const formData = new FormData();

    let obj = salaryEligibility[0];
    obj["employeeid"] = employeeid;
    formData.append("salaryEligibility", JSON.stringify({ obj }));
    try {
      const response = await axiosInstance.post(
        `${clientApi}api/Employee/UpdateSalaryEligibility`,
        formData,
        {
          params: { username: username, userid: account.employeeId },
          headers: { "Content-Type": "multipart/form-data" },
        }
      );
      return response.data;
    } catch (error) {
      console.error(error);
      return [];
    }
  };
  //#endregion

  //#region Post Employee Data
  const postUserData = async (userExists, userData) => {
    const formData = new FormData();
    formData.append('user', JSON.stringify({ userData }));
    try {
      const response = await axiosInstance.post(`${clientApi}api/Employee/EmployeeData`, formData, {
        params: { username: username, userExists: userExists, userid: account.employeeId },
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response;
    } catch (error) {
      console.error(error);
      return [];
    };
  };

  const saveEmplSpecialDuties = async (employeeID, company, specialDuties) => {
    const formData = new FormData();

    formData.append("specialDuties", JSON.stringify({ specialDuties }));
    try {
      const response = await axiosInstance.post(
        `${clientApi}api/Employee/SaveEmplSpecialDuties`,
        formData,
        {
          params: {
            username: username,
            userid: account.employeeId,
            employeeid: employeeID,
            company: company,
          },
          headers: { "Content-Type": "multipart/form-data" },
        }
      );
      return response;
    } catch (error) {
      console.error(error);
      return [];
    }
  };

  //#endregion

  //#region Promotion History
  const getPromHist = async (employeeId, company, rowguid, dept) => {
    let ret;
    await axiosInstance
      .get(`${clientApi}api/Employee/GetPromHist`, {
        params: {
          username: username,
          employeeid: employeeId,
          company: company,
          rowguid: rowguid,
          dept: dept,
        },
      })
      .then((response) => {
        if (response.status === 200) ret = response;
        else ret = [];
      })
      .catch((error) => {
        console.log(error);
        ret = [];
      });

    return ret;
  };

  const insertPromHist = async (promHist) => {
    const formData = new FormData();
    if (promHist.length === 0) return { status: 200 };
    promHist.forEach(item => {
      if (!item.rowguid) {
        item.rowguid = generateUniqueID();
      }
    });
    formData.append("promHist", JSON.stringify({ promHist }));
    try {
      const response = await axiosInstance.post(
        `${clientApi}api/Employee/InsertPromHist`,
        formData,
        {
          params: { username: username, userid: account.employeeId },
          headers: { "Content-Type": "multipart/form-data" },
        }
      );
      return response;
    } catch (error) {
      console.error(error);
      return [];
    }
  };

  const deletePromHist = async (promHist) => {
    const formData = new FormData();
    if (promHist.length === 0) return { status: 200 };
    formData.append("promHist", JSON.stringify({ promHist }));
    try {
      const response = await axiosInstance.post(
        `${clientApi}api/Employee/DeletePromHist`,
        formData,
        {
          params: { username: username, userid: account.employeeId },
          headers: { "Content-Type": "multipart/form-data" },
        }
      );
      return response;
    } catch (error) {
      console.error(error);
      return [];
    }
  };

  const updatePromHist = async (promHist) => {
    const formData = new FormData();
    if (promHist.length === 0) return { status: 200 };
    formData.append("promHist", JSON.stringify({ promHist }));
    try {
      const response = await axiosInstance.post(
        `${clientApi}api/Employee/UpdatePromHist`,
        formData,
        {
          params: { username: username, userid: account.employeeId },
          headers: { "Content-Type": "multipart/form-data" },
        }
      );
      return response;
    } catch (error) {
      console.error(error);
      return [];
    }
  };

  const loadEmployeeById = async (employeeId) => {
    const formData = new FormData();
    formData.append("employeeId", JSON.stringify({ employeeId }));
    try {
      const response = await axiosInstance.post(
        `${clientApi}api/Employee/GetEmployeeById`,
        formData,
        {
          params: { username: username, userid: account.employeeId },
          headers: { "Content-Type": "multipart/form-data" },
        }
      );
      return response.data;
    } catch (error) {
      console.error(error);
      return null; // Return null or an empty object if there's an error
    }
  };

  const getSalaryReviewById = async (id) => {
    try {
      const response = await axiosInstance.get(
        `${clientApi}api/Employee/GetSalaryReviewsById`,
        {
          params: { employeeId: id },
        }
      );
      return response.data;
    } catch (error) {
      console.error(`Error fetching salary review with ID ${id}:`, error);
      return null;
    }
  };

  const getSalaryComponent = async (emplSalaryId, salarySchemeId) => {
    try {
      const response = await axiosInstance.get(
        `${clientApi}api/Employee/GetSalaryComponent`,
        {
          params: {
            emplSalaryId: emplSalaryId,
            salarySchemeId: salarySchemeId,
          },
          // headers: { 'Content-Type': 'multipart/form-data' }
        }
      );
      return response.data;
    } catch (error) {
      console.error(
        `Error fetching salary review with ID ${emplSalaryId}:`,
        error
      );
      return null;
    }
  };
  const getEmplSalaryComponentCurrentReview = async (emplSalaryId) => {
    try {
      const response = await axiosInstance.get(
        `${clientApi}api/Employee/GetEmplSalaryComponentCurrentReview`,
        {
          params: { emplSalaryId: emplSalaryId },
          // headers: { 'Content-Type': 'multipart/form-data' }
        }
      );
      return response.data;
    } catch (error) {
      console.error(
        `Error fetching salary review with ID ${emplSalaryId}:`,
        error
      );
      return null;
    }
  };

  const getEmplSalaryComponentPreviousReview = async (
    salarySchemeId,
    employeeId,
    startdate
  ) => {
    const formattedDate = startdate;
    // console.log(`Fetching salary review for emplSalaryId: ${salarySchemeId}, employeeId: ${employeeId}, startdate: ${formattedDate}`);
    try {
      const response = await axiosInstance.get(
        `${clientApi}api/Employee/GetEmplSalaryComponentPreviousReview`,
        {
          params: {
            salarySchemeId: salarySchemeId,
            employeeId: employeeId,
            fdate: formattedDate,
          },
        }
      );
      if (!response.data || response.data.length === 0) {
        console.log("No previous data found.");
        return []; // return an empty array explicitly
      }
      console.log(`Received response:`, response.data);
      return response.data;
    } catch (error) {
      console.error(
        `Error fetching salary review with ID ${salarySchemeId}:`,
        error.response ? error.response.data : error.message
      );
      return null;
    }
  };

  const saveSalaryReview = async (review, id) => {
    try {
      const response = await axiosInstance.post(
        "/api/Employee/SaveSalaryReviews",
        review,
        {
          headers: { "Content-Type": "application/json" },
          params: { username: username, employeeId: id },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error creating salary review:", error);
      throw error;
    }
  };

  const deleteSalaryReview = async (id) => {
    const formData = new FormData();
    formData.append("id", JSON.stringify({ id }));
    try {
      const response = await axiosInstance.post(
        `${clientApi}api/Employee/DeleteSalaryReviews`,
        formData,
        {
          params: { username: username, userid: account.employeeId },
          headers: { "Content-Type": "multipart/form-data" },
        }
      );
      return response.data;
    } catch (error) {
      console.error;
      return null;
    }
  };

  const updateSalaryComponent = async (components) => {
    try {
      console.log("Sending request to update salary components:", components);
      const response = await axiosInstance.post(
        `${clientApi}api/Employee/UpdateSalaryComponent`,
        components,
        {
          headers: { "Content-Type": "application/json" },
        }
      );
      return response;
    } catch (error) {
      console.error("Update salary error", error);
      if (error.response) {
        console.error("backend error response:", error.response.data);
      }
      return null;
    }
  };

  //#endregion

  //#region Academic Qualifications
  const getAcademicQual = async (emplId) => {
    try {
      setIsLoading(true)
      const response = await axiosInstance.get(
        "/api/Employee/GetAcademicQual",
        {
          headers: { "Content-Type": "application/json" },
          params: { username: username, employeeId: emplId },
        }
      );
      setIsLoading(false)
      return response.data;
    } catch (error) {
      setIsLoading(false)
      console.error("Error creating salary review:", error);
      throw error;
    }
  };

  const saveAcademicQual = async (academicQual) => {
    let quals = [];
    let ret = false;
    const keys = Object.keys(academicQual);
    keys.forEach((key) => {
      const nestedObject = academicQual[key];
      quals.push(nestedObject);
    });
    setIsLoading(true);
    await axiosInstance
      .put(`${clientApi}api/Employee/saveAcademicQual`, quals, {
        params: { employeeid: account.employeeId, username: account.username },
      })
      .then((response) => {
        if (response.status === 200) {
          ret = true;
        }
        else
          ret = false;
      })
      .catch((error) => {
        console.log(error);
        ret = false;
      });

    setIsLoading(false);
    return ret;
  };

  const deleteAcademicQual = async (academicQualRowGuids) => {
    setIsLoading(true);
    let ret;
    await axiosInstance
      .delete(`${clientApi}api/Employee/deleteAcademicQual`, {
        params: { username: account.username },
        data: academicQualRowGuids,
      })
      .then((response) => {
        if (response.status === 200) ret = true;
        else ret = false;
      })
      .catch((error) => {
        console.log(error);
        ret = false;
      });
    setIsLoading(false);
    return ret;
  };

  const getAcademicQualHelper = async () => {
    let ret;
    setIsLoading(true)
    await axiosInstance.get(
      "/api/Employee/GetAcademicQualHelper",
      {
        headers: { "Content-Type": "application/json" },
        params: { username: username },
      }
    ).then((response) => {
      if (response.status === 200)
        ret = response.data;
      else ret = [];
      setIsLoading(false)
    })
      .catch((error) => {
        console.log(error);
        ret = [];
        setIsLoading(false)
      });

    return ret;
  };
  //#endregion

  //#region Employee Wizard
  const getEmployeeWizard = async ({ type }) => {
    let ret;
    await axiosInstance.get(`${clientApi}api/Employee/WizardSteps`, {
      params: {
        username: username,
        type: type
      },
    })
      .then((response) => {
        if (response.status === 200)
          ret = response;
        else ret = [];
      })
      .catch((error) => {
        console.log(error);
        ret = [];
      });

    return ret;
  }
  //#endregion

  //#region Employee TimeSchemes
  /**
  * CreateEmployeeTimeschemesRequest
  *
  * @param {Object} payload
  * @property {Record<number, string>} timeschemes - Map of month (1..12) -> timeschemeId
  * @property {string} employeeID
  * @property {string} company
  * @property {string} branch
  * @property {string} location
  * @property {string} buildingFloor
  */
  const postTimeSchemes = async (payload) => {
    try {
      response = await axiosInstance.post(`${clientApi}api/Employee/EmplTimeSchemes`, payload);
      return response;
    } catch (error) {
      console.error(error);
      return response;
    };
  }

  //#endregion

  //#region UserRole

  //#region Employee Authorization
  const postEmplAuth = async (includedEmployees, rulesData, excludedEmployees, selectedEmployee) => {
    const formData = new FormData();
    formData.append('includedEmployees', JSON.stringify({ includedEmployees }));
    formData.append('excludedEmployees', JSON.stringify({ excludedEmployees }));
    formData.append('rules', JSON.stringify({ rulesData }));
    try {
      const response = await axiosInstance.post(`${clientApi}api/Employee/PostEmplAuth`, formData, {
        params: { username: username, userid: account.employeeId, employeeID: selectedEmployee },
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response;
    } catch (error) {
      console.error(error);
      return [];
    };
  }

  const getEmplAuthRules = async (employeeId) => {
    try {
      const response = await axiosInstance.get(`${clientApi}api/Employee/GetEmplAuthRules`, {
        params: { username: username, employeeID: employeeId },
      });
      return response.data;
    } catch (error) {
      console.error(error);
      return [];
    }
  };
 
  const deleteEmplAuthRule = async (ui, employeeID) => {
    const formData = new FormData();
    formData.append('ui', ui);
    try {
      const response = await axiosInstance.post(`${clientApi}api/Employee/DeleteEmplAuthRule`, formData, {
        params: { username: username, userid: account.employeeId, employeeID: employeeID },
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response;
    } catch (error) {
      console.error(error);
      return null;
    }
  };

  //#endregion
  const updateUserRole = async (data) => {
    const formData = new FormData();
    formData.append('data', JSON.stringify({ data }));
    try {
      const response = await axiosInstance.post(`${clientApi}api/Employee/PostUserRole`, formData, {
        params: { username: username, userid: account.employeeId },
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response;
    } catch (error) {
      console.error(error);
      return [];
    };
  }

  const issuePassword = async (employeeName, employeeId, username, userid) => {
    try {
      const response = await axiosInstance.get(`${clientApi}api/Employee/IssueRandomPassword`, {
        params: { employeeName: employeeName, employeeID: employeeId, username: username, userid: account.employeeId },
      });
      return response;
    } catch (error) {
      console.error(error);
      return null;
    };
  }

  const sendUsername = async (username, employeeId) => {
    try {
      const response = await axiosInstance.get(`${clientApi}api/Employee/sendUsername`, {
        params: { username: username, employeeid: employeeId, userid: account.employeeId },
      });
      return response;
    } catch (error) {
      console.error(error);
      return null;
    };
  }
  //#endregion

  //#region Validators
  const ValidateUniqueConstraints = async ({ m_bExists, id, lastName, fullName, username, employeeNumber, email, timeCard }) => {
    let ret;
    await axiosInstance.get(`${clientApi}api/Employee/ValidateUniqueConstraints`, {
      params: {
        m_bExists: m_bExists,
        id: id,
        lastName: lastName,
        name: fullName,
        username: username,
        employeeNumber: employeeNumber,
        email: email,
        timeCard: timeCard
      },
    })
      .then((response) => {
        if (response.status === 200)
          ret = response;
        else ret = [];
      })
      .catch((error) => {
        console.log(error);
        ret = [];
      });

    return ret;
  }
  //#endregion

  //#region Reports To 
  const insertReportsTo = async (data) => {
    try {
      const formData = new FormData();
      let reportsTo = data?.reportsTo;
      formData.append('data', JSON.stringify({ reportsTo }));
      const response = await axiosInstance.post(`${clientApi}api/Employee/ReportsTo`, formData, {
        params: { username: username, userid: account.employeeId },
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response;
    } catch (error) {
      console.error(error);
      return [];
    };
  }
  //#endregion

  const insertEmplWorkflow = async (workflows) => {
    const data = workflows?.workflows || [];
    const formData = new FormData();
    formData.append('workflows', JSON.stringify({ data }));
    formData.append('employeeId', data[0]?.employeeID || '');
    try {
      const response = await axiosInstance.post(`${clientApi}api/Employee/PostEmplWorkflows`, formData, {
        params: { username: username, userid: account.employeeId },
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response;
    } catch (error) {
      console.error(error);
      return [];
    };
  }
  //#endregion

  //#region Comp Auth
  const insertCompAuth = async (auth) => {
    const data = auth?.selectedCompanies;
    const formData = new FormData();
    formData.append('auth', JSON.stringify({ data }));
    try {
      const response = await axiosInstance.post(`${clientApi}api/Employee/PostCompAuth`, formData, {
        params: { username: username, userid: account.employeeId },
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response;
    } catch (error) {
      console.error(error);
      return [];
    };
  }
  //#endregion

  //#region Leave Analysis
  const createLeaveAnalysis = async (employeeId, company, startDate, endDate, irollyear = 0) => {
    const params = {
      username: username,
      userid: account.employeeId,
      employeeId: employeeId,
      company: company,
      startDate,
      endDate,
      irollyear: irollyear
    };
    try {
      const response = await axiosInstance.get(`${clientApi}api/Employee/CreateLeaveAnalysis`, {
        params: params,
      });
      return response;
    } catch (error) {
      console.error(error);
      return [];
    };
  }

  //Save and Delete is not finalized
  const SaveAbsenceAnalysis = async (analysis, id) => {
    try {
      const response = await axiosInstance.post(
        "/api/Employee/SaveAbsenceAnalysis",
        analysis,
        {
          headers: { "Content-Type": "application/json" },
          params: { username: username, employeeId: id },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error creating Absence analysis:", error);
      throw error;
    }
  };


  const DeleteAbsenceAnalysis = async (id) => {
    const formData = new FormData();
    formData.append("id", JSON.stringify({ id }));
    try {
      const response = await axiosInstance.post(
        `${clientApi}api/Employee/DeleteAbsenceAnalysis`,
        formData,
        {
          params: { username: username, userid: account.employeeId },
          headers: { "Content-Type": "multipart/form-data" },
        }
      );
      return response.data;
    } catch (error) {
      console.error;
      return null;
    }
  };

  const GetAbsenceAnalysis = async (id, Doa, EndDate) => {
    try {
      const response = await axiosInstance.get(
        `${clientApi}api/Employee/GetAbsenceAnalysis`,
        {
          params: {
            username: username,
            employeeId: id,
            Doa: Doa,
            EndDate: EndDate
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error(`Error fetching absence analysis with ID ${id}:`, error);
      return null;
    }
  };

  const leaveAnalysisGetById = async (Id, fiscalYear) => {
    const fy = String(fiscalYear);
    try {
      const response = await axiosInstance.get(
        `${clientApi}api/Employee/PersonalLeaveBalances`,
        {
          headers: { Authorization: `Bearer ${user.token}` },
          params: {
            username: account.username,
            employeeId: Id,
            fiscalYear: fy,
          },
        }
      );
      return response.data;
    } catch (error) {
      if (error?.response?.status === 401) {
        refreshSignIn(user, clientApi);
      }
      console.error("leaveaAalysisGetById failed:", error)
      return null;
    }
  };

  const reconcileLeaveBalance = async (employeeId, abscode, date, companyId, broughtForward, paid, remaining, allowance, fiscalYear) => {
    try {
      const response = await axiosInstance.post(
        `${clientApi}api/Employee/ReconcileLeaveBalance`,
        {
          date: date,
          allowance: allowance,
          broughtForward: broughtForward,
          paid: paid,
          remaining: remaining,
        },
        {
          params: {
            username: account.username,
            userid: account.employeeId,
            employeeid: employeeId,
            abscode: abscode,
            companyid: companyId,
            fiscalyear: fiscalYear
          }
        }
      );
      return response;
    } catch (error) {
      console.error("Error reconciling leave balance:", error);
      throw error;
    }
  };
  //#endregion 

  const getEmployeeWorkflows = async ({ company, employeeId }) => {
    let ret;
    await axiosInstance.get(`${clientApi}api/Employee/GetEmployeeWorkflows`, {
      params: {
        employeeId: employeeId,
        username: username,
        company: company,
      },
    })
      .then((response) => {
        if (response.status === 200)
          ret = response;
        else ret = [];
      })
      .catch((error) => {
        console.log(error);
        ret = [];
      });

    return ret;
  }


  const GetAdditionalQual = async (emplId) => {
    try {
      setIsLoading(true)
      const response = await axiosInstance.get(
        "/api/Employee/GetAdditionalQual",
        {
          headers: { "Content-Type": "application/json" },
          params: { username: username, employeeId: emplId },
        }
      );
      setIsLoading(false)
      return response.data;
    } catch (error) {
      setIsLoading(false)
      console.error("Error creating additional Qualification:", error);
      throw error;
    }
  };

  function transformQuals(quals) {
    return [{
      EmployeeId: quals.employeeId,
      Date: quals.date ? new Date(quals.date).toISOString() : null,
      Qualification: quals.qualification,
      Subject: quals.subject,
      Grade: quals.grade,
      Status: quals.status,
      UserId: quals.userId,
      Level: quals.level,
      Doc: quals.doc,
      Data: quals.data ? btoa(quals.data) : null,
      Type: quals.type !== undefined && quals.type !== null ? String(quals.type) : '',
      RowGuid: quals.rowGuid,
      FileName: quals.fileName,
      FileType: quals.fileType,
      FileData: quals.fileData
    }];
  }

  const SaveAdditionalQual = async (additionalQual) => {
    let quals = [];
    let ret = false;
    const keys = Object.keys(additionalQual);
    keys.forEach((key) => {
      const nestedObject = additionalQual[key];
      quals.push(nestedObject);
    });
    setIsLoading(true);

    await axiosInstance
      .put(`${clientApi}api/Employee/SaveAdditionalQual`, quals, {
        params: { employeeid: account.employeeId, username: account.username },
      })
      .then((response) => {
        if (response.status === 200) {
          ret = true;
        }
        else
          ret = false;
      })
      .catch((error) => {
        console.log(error);
        ret = false;
      });

    setIsLoading(false);
    return ret;
  };

  const deleteAdditionalQual = async (additionalQualRowGuids) => {
    setIsLoading(true);
    let ret;
    await axiosInstance
      .delete(`${clientApi}api/Employee/deleteAdditionalQual`, {
        params: { username: account.username },
        data: additionalQualRowGuids,
      })
      .then((response) => {
        if (response.status === 200) ret = true;
        else ret = false;
      })
      .catch((error) => {
        console.log(error);
        ret = false;
      });
    setIsLoading(false);
    return ret;
  };

  const GetAdditionalQualHelper = async () => {
    let ret;
    setIsLoading(true)
    await axiosInstance.get(
      "/api/Employee/GetAdditionalQualHelper",
      {
        headers: { "Content-Type": "application/json" },
        params: { username: username },
      }
    ).then((response) => {
      if (response.status === 200)
        ret = response.data;
      else ret = [];
      setIsLoading(false)
    })
      .catch((error) => {
        console.log(error);
        ret = [];
        setIsLoading(false)
      });

    return ret;
  };

  const GetAdditionalDocuments = async (id) => {
    try {
      const response = await axiosInstance.get(
        `${clientApi}api/Employee/GetAdditionalDocuments`,
        {
          params: {
            username: username,
            employeeId: id
          },
        }
      );
      console.log('GetAdditionalDocuments response:', response.data);
      return response.data;
    } catch (error) {
      console.error(`Error fetching documents:`, error);
      return null;
    }
  };


  const GetSpecialDocuments = async (emplId) => {
    try {
      setIsLoading(true)
      const response = await axiosInstance.get(
        "api/Employee/GetSpecialDocuments",
        {
          headers: { "Content-Type": "application/json" },
          params: { username: username, employeeId: emplId },
        }
      );
      setIsLoading(false)
      return response.data;
    } catch (error) {
      setIsLoading(false)
      console.error("Error fetching special document:", error);
      throw error;
    }
  };

  const SaveSpecialDocuments = async (specialDoc) => {
    setIsLoading(true);
    let ret = false;
    const docs = Object.values(specialDoc);

    await axiosInstance
      .put(`${clientApi}api/Employee/SaveSpecialDocuments`, docs, {
        params: { employeeId: account.employeeId, username: account.username },
      })
      .then((response) => {
        if (response.status === 200) {
          ret = true;
        }
        else
          ret = false;
      })
      .catch((error) => {
        console.error(error);
        ret = false;
      });

    setIsLoading(false);
    return ret;
  };

  const DeleteSpecialDocuments = async (specialDocsRowGuids) => {
    setIsLoading(true);
    let ret;
    await axiosInstance
      .delete(`${clientApi}api/Employee/DeleteSpecialDocuments`, {
        params: { username: account.username },
        data: specialDocsRowGuids,
      })
      .then((response) => {
        if (response.status === 200) ret = true;
        else ret = false;
      })
      .catch((error) => {
        console.log(error);
        ret = false;
      });
    setIsLoading(false);
    return ret;
  };

  const GetSpecialDocHelper = async () => {
    let ret;
    setIsLoading(true)
    await axiosInstance.get(
      "/api/Employee/GetSpecialDocHelper",
      {
        headers: { "Content-Type": "application/json" },
        params: { username: username },
      }
    ).then((response) => {
      if (response.status === 200)
        ret = response.data;
      else ret = [];
      setIsLoading(false)
    })
      .catch((error) => {
        console.log(error);
        ret = [];
        setIsLoading(false)
      });

    return ret;
  };

  const deleteEmplWorkflows = async (emplWorkflowRowGuids) => {
    setIsLoading(true);
    let ret;
    await axiosInstance
      .post(`${clientApi}api/Employee/DeleteEmplWorkflows`, emplWorkflowRowGuids, {
        params: { username: account.username },
      })
      .then((response) => {
        if (response.status === 200) ret = true;
        else ret = false;
      })
      .catch((error) => {
        console.error('Error deleting employee workflows:', error);
        ret = false;
      });
    setIsLoading(false);
    return ret;
  };


  // For Exit Process
  const GetExitProcessHelper = async () => {
    let ret;
    setIsLoading(true)
    await axiosInstance.get(
      "/api/Employee/GetExitProcessHelper",
      {
        headers: { "Content-Type": "application/json" },
        params: { username: username },
      }
    ).then((response) => {
      if (response.status === 200)
        ret = response.data;
      else ret = [];
      setIsLoading(false)
    })
      .catch((error) => {
        ret = [];
        setIsLoading(false)
      });

    return ret;
  };

  const GetExitProcess = async (emplId) => {
    try {
      setIsLoading(true)
      const response = await axiosInstance.get(
        "api/Employee/GetExitProcess",
        {
          headers: { "Content-Type": "application/json" },
          params: { username: username, employeeId: emplId },
        }
      );
      setIsLoading(false)
      return response.data;
    } catch (error) {
      setIsLoading(false)
      console.error("Error fetching special document:", error);
      throw error;
    }
  };

  const SaveExitProcess = async (exitProcess) => {
    setIsLoading(true);
    let ret = false;
    const data = Object.values(exitProcess);

    await axiosInstance
      .put(`${clientApi}api/Employee/SaveExitProcess`, data, {
        params: { employeeId: account.employeeId, username: account.username },
      })
      .then((response) => {
        if (response.status === 200) {
          ret = true;
        }
        else
          ret = false;
      })
      .catch((error) => {
        console.error(error);
        ret = false;
      });

    setIsLoading(false);
    return ret;
  };

  const DeleteExitProcess = async (exitProcessRowGuids) => {
    setIsLoading(true);
    let ret;
    await axiosInstance
      .delete(`${clientApi}api/Employee/DeleteExitProcess`, {
        params: { username: account.username },
        data: exitProcessRowGuids,
      })
      .then((response) => {
        if (response.status === 200) ret = true;
        else ret = false;
      })
      .catch((error) => {
        console.log(error);
        ret = false;
      });
    setIsLoading(false);
    return ret;
  };

  //#region Reports To
  const getReportsToHelper = async (selectedCompany) => {
    let ret;
    setIsLoading(true)
    await axiosInstance.get(
      "/api/Employee/getReportsToHelper",
      {
        headers: { "Content-Type": "application/json" },
        params: { username: username, company: selectedCompany, employeeId: account.employeeId },
      }
    ).then((response) => {
      if (response.status === 200)
        ret = response.data;
      else
        ret = false;
      setIsLoading(false);
    })
      .catch((error) => {
        console.log(error);
        ret = false;
        setIsLoading(false);
      });

    return ret;
  };

  const getReportsTo = async (emplId) => {
    try {
      setIsLoading(true)
      const response = await axiosInstance.get(
        "/api/Employee/GetReportsTo",
        {
          headers: { "Content-Type": "application/json" },
          params: { username: username, employeeId: emplId },
        }
      );
      setIsLoading(false);
      return response.data;
    } catch (error) {
      setIsLoading(false);
      console.error("Error fetching Reports To:", error);
      throw error;
    }
  };

  const saveReportsTo = async (reportsTo) => {
    let repTo = [];
    let ret = false;
    const keys = Object.keys(reportsTo);
    keys.forEach((key) => {
      const nestedObject = reportsTo[key];
      repTo.push(nestedObject);
    });
    setIsLoading(true);
    await axiosInstance
      .put(`${clientApi}api/Employee/SaveReportsTo`, repTo, {
        params: { employeeid: account.employeeId, username: account.username },
      })
      .then((response) => {
        if (response.status === 200) {
          ret = true;
        }
        else
          ret = false;
      })
      .catch((error) => {
        console.log(error);
        ret = false;
      });

    setIsLoading(false);
    return ret;
  };

  const deleteReportsTo = async (reportsToRowGuids) => {
    setIsLoading(true);
    let ret;
    await axiosInstance
      .delete(`${clientApi}api/Employee/DeleteReportsTo`, {
        params: { username: account.username },
        data: reportsToRowGuids,
      })
      .then((response) => {
        if (response.status === 200) ret = true;
        else ret = false;
      })
      .catch((error) => {
        console.log(error);
        ret = false;
      });
    setIsLoading(false);
    return ret;
  };

  const insertReportsToWizard = async (data) => {
    try {
      const formData = new FormData();
      let reportsTo = data?.reportsTo;
      formData.append('data', JSON.stringify({ reportsTo }));
      const response = await axiosInstance.post(`${clientApi}api/Employee/ReportsToWizard`, formData, {
        params: { username: username, userid: account.employeeId },
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response;
    } catch (error) {
      console.error(error);
      return [];
    };
  }
  //#endregion


  //#region Bodies
  const getProfBodiesValueList = async (par, filter = "") => {
    let ret;
    setIsLoading(true);
    await axiosInstance
      .get(`${clientApi}api/Employee/ProfBodies/ValueList`, {
        params: {
          username: username,
          par: par,
          filter: filter,
        },
      })
      .then((response) => {
        if (response.status === 200) ret = response.data;
        else ret = [];
        setIsLoading(false);
      })
      .catch((error) => {
        console.log(error);
        ret = [];
        setIsLoading(false);
      });

    return ret;
  };

  const getProfBodiesDescription = async (profbody) => {
    try {
      setIsLoading(true);
      const response = await axiosInstance.get(
        `${clientApi}api/Employee/ProfBodies/Description`,
        {
          params: { username: username, profbody: profbody },
        }
      );
      setIsLoading(false);
      return response.data;
    } catch (error) {
      setIsLoading(false);
      console.error("Error fetching professional body description:", error);
      return null;
    }
  };

  const GetEmplProfBodiesHelper = async () => {
    let ret;
    setIsLoading(true);
    await axiosInstance
      .get(`${clientApi}api/Employee/GetEmplProfBodiesHelper`, {
        headers: { "Content-Type": "application/json" },
        params: { username: username },
      })
      .then((response) => {
        if (response.status === 200) ret = response.data;
        else ret = [];
        setIsLoading(false);
      })
      .catch((error) => {
        console.log(error);
        ret = [];
        setIsLoading(false);
      });

    return ret;
  };

  const GetEmplProfBodies = async (emplId) => {
    try {
      setIsLoading(true);
      const response = await axiosInstance.get(
        `${clientApi}api/Employee/GetEmplProfBodies`,
        {
          headers: { "Content-Type": "application/json" },
          params: { username: username, employeeId: emplId },
        }
      );
      setIsLoading(false);
      return response.data;
    } catch (error) {
      setIsLoading(false);
      console.error("Error fetching professional bodies:", error);
      throw error;
    }
  };

  const SaveEmplProfBodies = async (profBodies) => {
    let bodies = [];
    let ret = false;
    const keys = Object.keys(profBodies);
    keys.forEach((key) => {
      const nestedObject = profBodies[key];
      bodies.push(nestedObject);
    });
    setIsLoading(true);
    await axiosInstance
      .put(`${clientApi}api/Employee/SaveEmplProfBodies`, bodies, {
        params: { employeeid: account.employeeId, username: account.username },
      })
      .then((response) => {
        if (response.status === 200) ret = true;
        else ret = false;
      })
      .catch((error) => {
        console.log(error);
        ret = false;
      });

    setIsLoading(false);
    return ret;
  };

  const deleteEmplProfBodies = async (profBodiesRowGuids) => {
    setIsLoading(true);
    let ret;
    await axiosInstance
      .delete(`${clientApi}api/Employee/deleteEmplProfBodies`, {
        params: { username: account.username },
        data: profBodiesRowGuids,
      })
      .then((response) => {
        if (response.status === 200) ret = true;
        else ret = false;
      })
      .catch((error) => {
        console.log(error.response?.data);
        ret = false;
      });
    setIsLoading(false);
    return ret;
  };

  //#endregion

  //#region Inventory
  const getEmplInventory = async (emplId) => {
    try {
      setIsLoading(true)
      const response = await axiosInstance.get(
        "/api/Employee/GetEmployeeInventory",
        {
          headers: { "Content-Type": "application/json" },
          params: { username: username, employeeId: emplId },
        }
      );
      setIsLoading(false);
      return response.data;
    } catch (error) {
      setIsLoading(false);
      console.error(error);
      throw error;
    }
  };
  //#endregion

  return {
    getBranches, getLocations, getDepartments, getSeniorityLevels, getEmploymentTypes, getJobTitles, getEmployees, getEmployeesInCompany,
    getEmployeeData, getMandatoryFields, postUserData, getDropdownValues, getFamilyDetails, getEmplDepts,
    getEmployeeAccumulatedBalances, CalculateEmplRevisedAllowance, reconcileBalances, getLimitedData,
    getTimescheme, updateEmplDepts, insertEmplDepts, deleteEmplDepts,
    insertFamilyDetails, deleteFamilyDetails, updateFamilyDetails, updateSalaryEligibility,
    getSchedule, getMedicalScheme, getProvidentFund,
    insertSchedule, insertMedicalScheme, insertProvidentFund,
    updateSchedule, updateMedicalScheme, updateProvidentFund,
    deleteSchedule, deleteMedicalScheme, deleteProvidentFund,
    getPromHist, insertPromHist, updatePromHist, deletePromHist,
    saveEmplSpecialDuties, getEmployeeWizard, ValidateUniqueConstraints,
    postTimeSchemes, updateUserRole, issuePassword, sendUsername,
    postEmplAuth, getEmplAuthRules, deleteEmplAuthRule, insertReportsToWizard, insertEmplWorkflow,
    insertCompAuth, createLeaveAnalysis, saveSalaryReview, updateSalaryComponent,
    deleteSalaryReview, reconcileLeaveBalance,
    getEmplSalaryComponentPreviousReview,
    getEmployeeWorkflows,
    getEmplSalaryComponentCurrentReview, getSalaryReviewById, getSalaryComponent, loadEmployeeById, getAcademicQualHelper, getAcademicQual, saveAcademicQual,
    deleteAcademicQual, isLoading, GetSpecialDocuments, SaveSpecialDocuments, DeleteSpecialDocuments, GetSpecialDocHelper,
    SaveAbsenceAnalysis, DeleteAbsenceAnalysis, GetAbsenceAnalysis, leaveAnalysisGetById,
    GetAdditionalQual, SaveAdditionalQual, deleteAdditionalQual, GetAdditionalQualHelper, GetAdditionalDocuments, deleteEmplWorkflows,
    GetExitProcess, GetExitProcessHelper, SaveExitProcess, DeleteExitProcess, getReportsToHelper, getReportsTo, saveReportsTo, deleteReportsTo, getEmployeeIdBasedOnLocation,
    getEmplInventory,
    getProfBodiesValueList, getProfBodiesDescription, GetEmplProfBodiesHelper, GetEmplProfBodies, SaveEmplProfBodies, deleteEmplProfBodies
  };
};
